import {
  Box,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Pagination,
  CircularProgress,
  Typography,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Title as TitleIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useState, useImperativeHandle, forwardRef } from "react";
import knowledgebaseAgentService from "../../../services/knowledgebase-agent.service";

/* ---------------- PLAN CONFIG ---------------- */
const PLAN_LIMITS = {
  free: 10,
  professional: 50,
  enterprise: Infinity,
};

const SimplifiedWelcomeStep = forwardRef(
  ({ formData, updateFormData, mode = "add" }, ref) => {
    const [errors, setErrors] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [dialogError, setDialogError] = useState("");

    const rowsPerPage = 10;

    /* ---------------- USER & PLAN ---------------- */
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userPlan = user?.plan || "free";
    const maxSelectable = PLAN_LIMITS[userPlan] ?? 10;

    /* ---------------- HELPERS ---------------- */
    const handleChange = (field, value) => {
      updateFormData({ [field]: value });
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const isOnlyLetters = (text) => /^[A-Za-z ]+$/.test(text);
    const isValidUrl = (value) => {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    };
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isValidPhone = (phone) => /^[0-9+\-\s()]{7,20}$/.test(phone);

    /* ---------------- FETCH SITEMAP ---------------- */
    const fetchSitemapLinks = async () => {
      if (mode === "edit") return;

      if (!isValidUrl(formData.websiteUrl)) return;

      try {
        setLoading(true);
        setDialogError("");

        const result = await knowledgebaseAgentService.fetchSitemapLinks(
          formData.websiteUrl
        );

        updateFormData({
          sitemapLinks: result.urls || [],
          selectedSitemapLinks: [],
        });

        setPage(1);
        setOpenDialog(true);
      } catch (error) {
        setErrors({
          websiteUrl: error?.message || "Failed to fetch website pages",
        });
      } finally {
        setLoading(false);
      }
    };

    /* ---------------- TOGGLE SELECTION ---------------- */
    const toggleSitemapLink = (url) => {
      const selected = formData.selectedSitemapLinks || [];

      // Remove
      if (selected.includes(url)) {
        updateFormData({
          selectedSitemapLinks: selected.filter((u) => u !== url),
        });
        setDialogError("");
        return;
      }

      // Limit reached
      if (selected.length >= maxSelectable) {
        if (userPlan !== "enterprise") {
          setDialogError(
            `Your ${userPlan} plan allows selecting up to ${maxSelectable} URLs`
          );
        }
        return;
      }

      updateFormData({
        selectedSitemapLinks: [...selected, url],
      });
      setDialogError("");
    };

    /* ---------------- DIALOG DONE ---------------- */
    const handleDialogDone = () => {
      if (mode === "edit") {
        setOpenDialog(false);
        return;
      }

      if (!formData.selectedSitemapLinks?.length) {
        setErrors({
          websiteUrl: "Select at least one website page",
        });
        return;
      }
      setOpenDialog(false);
    };

    /* ---------------- STEP VALIDATION ---------------- */
    useImperativeHandle(ref, () => ({
      validateStep: () => {
        const newErrors = {};

        if (!formData.knowledgeName?.trim())
          newErrors.knowledgeName = "Knowledge Base name is required";
        else if (!isOnlyLetters(formData.knowledgeName))
          newErrors.knowledgeName = "Only letters and spaces allowed";

        if (!formData.title?.trim())
          newErrors.title = "Widget title is required";
        else if (!isOnlyLetters(formData.title))
          newErrors.title = "Only letters and spaces allowed";

        if (mode === "add") {
          if (!formData.websiteUrl?.trim())
            newErrors.websiteUrl = "Website URL is required";
          else if (!isValidUrl(formData.websiteUrl))
            newErrors.websiteUrl = "Enter a valid website URL";
        }

        if (!formData.contactEmail?.trim())
          newErrors.contactEmail = "Email is required";
        else if (!isValidEmail(formData.contactEmail))
          newErrors.contactEmail = "Enter a valid email";

        if (!formData.phoneNumber?.trim())
          newErrors.phoneNumber = "Phone number is required";
        else if (!isValidPhone(formData.phoneNumber))
          newErrors.phoneNumber = "Enter a valid phone number";

        if (mode === "add" && !formData.selectedSitemapLinks?.length) {
          newErrors.websiteUrl = "Select at least one website page";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
    }));

    const selectedCount = formData.selectedSitemapLinks?.length || 0;

    return (
      <Box display="flex" flexDirection="column" gap={3}>
        {/* ---------- FORM FIELDS ---------- */}
        <TextField
          label="Knowledge Base Name *"
          value={formData.knowledgeName || ""}
          onChange={(e) => handleChange("knowledgeName", e.target.value)}
          error={!!errors.knowledgeName}
          helperText={errors.knowledgeName}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TitleIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Description"
          value={formData.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          multiline
          minRows={3}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DescriptionIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Email *"
          value={formData.contactEmail || ""}
          onChange={(e) => handleChange("contactEmail", e.target.value)}
          error={!!errors.contactEmail}
          helperText={errors.contactEmail}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Phone Number *"
          value={formData.phoneNumber || ""}
          onChange={(e) => handleChange("phoneNumber", e.target.value)}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Widget Title *"
          value={formData.title || ""}
          onChange={(e) => handleChange("title", e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TitleIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Website URL *"
          value={formData.websiteUrl || ""}
          onChange={(e) => handleChange("websiteUrl", e.target.value)}
          onBlur={mode === "add" ? fetchSitemapLinks : undefined}
          disabled={mode === "edit"}
          error={!!errors.websiteUrl}
          helperText={errors.websiteUrl}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LanguageIcon />
              </InputAdornment>
            ),
            endAdornment: loading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />
        {/* ---------- SITEMAP DIALOG ---------- */}
        <Dialog open={openDialog && mode === "add"} maxWidth="md" fullWidth>
          <DialogTitle sx={{ position: "relative", pr: 5 }}>
            Select Website Pages
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Typography variant="body2" mb={1}>
              Selected {selectedCount}
              {userPlan !== "enterprise" && ` / ${maxSelectable}`}
            </Typography>

            {dialogError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}

            <List>
              {(formData.sitemapLinks || [])
                .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                .map((url) => {
                  const isChecked =
                    formData.selectedSitemapLinks?.includes(url);
                  const isDisabled =
                    !isChecked &&
                    userPlan !== "enterprise" &&
                    selectedCount >= maxSelectable;

                  return (
                    <ListItem key={url}>
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={() => toggleSitemapLink(url)}
                      />
                      <ListItemText primary={url} />
                    </ListItem>
                  );
                })}
            </List>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                page={page}
                onChange={(_, value) => setPage(value)}
                count={Math.ceil(
                  (formData.sitemapLinks?.length || 0) / rowsPerPage
                )}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleDialogDone}>Done</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

SimplifiedWelcomeStep.displayName = "SimplifiedWelcomeStep";
export default SimplifiedWelcomeStep;

// ==========================
// import {
//   Box,
//   TextField,
//   InputAdornment,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Checkbox,
//   List,
//   ListItem,
//   ListItemText,
//   Pagination,
//   CircularProgress,
//   Typography,
//   Button,
//   IconButton,
//   Alert,
// } from "@mui/material";
// import {
//   Title as TitleIcon,
//   Description as DescriptionIcon,
//   Email as EmailIcon,
//   Phone as PhoneIcon,
//   Language as LanguageIcon,
//   Close as CloseIcon,
// } from "@mui/icons-material";
// import { useState, useImperativeHandle, forwardRef } from "react";
// import knowledgebaseAgentService from "../../../services/knowledgebase-agent.service";

// /* ---------------- PLAN CONFIG ---------------- */
// const PLAN_LIMITS = {
//   free: 10,
//   professional: 50,
//   enterprise: Infinity,
// };

// const SimplifiedWelcomeStep = forwardRef(
//   ({ formData, updateFormData }, ref) => {
//     const [errors, setErrors] = useState({});
//     const [openDialog, setOpenDialog] = useState(false);
//     const [loading, setLoading] = useState(false);
//     const [page, setPage] = useState(1);
//     const [dialogError, setDialogError] = useState("");

//     const rowsPerPage = 10;

//     /* ---------------- USER & PLAN ---------------- */
//     const user = JSON.parse(localStorage.getItem("user") || "{}");
//     const userPlan = user?.plan || "free";
//     const maxSelectable = PLAN_LIMITS[userPlan] ?? 10;

//     /* ---------------- HELPERS ---------------- */
//     const handleChange = (field, value) => {
//       updateFormData({ [field]: value });
//       setErrors((prev) => ({ ...prev, [field]: "" }));
//     };

//     const isOnlyLetters = (text) => /^[A-Za-z ]+$/.test(text);
//     const isValidUrl = (value) => {
//       try {
//         new URL(value);
//         return true;
//       } catch {
//         return false;
//       }
//     };
//     const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
//     const isValidPhone = (phone) => /^[0-9+\-\s()]{7,20}$/.test(phone);

//     /* ---------------- FETCH SITEMAP ---------------- */
//     const fetchSitemapLinks = async () => {
//       if (!isValidUrl(formData.websiteUrl)) return;

//       try {
//         setLoading(true);
//         setDialogError("");

//         const result = await knowledgebaseAgentService.fetchSitemapLinks(
//           formData.websiteUrl
//         );

//         updateFormData({
//           sitemapLinks: result.urls || [],
//           selectedSitemapLinks: [],
//         });

//         setPage(1);
//         setOpenDialog(true);
//       } catch (error) {
//         setErrors({
//           websiteUrl: error?.message || "Failed to fetch website pages",
//         });
//       } finally {
//         setLoading(false);
//       }
//     };

//     /* ---------------- TOGGLE SELECTION ---------------- */
//     const toggleSitemapLink = (url) => {
//       const selected = formData.selectedSitemapLinks || [];

//       // Remove
//       if (selected.includes(url)) {
//         updateFormData({
//           selectedSitemapLinks: selected.filter((u) => u !== url),
//         });
//         setDialogError("");
//         return;
//       }

//       // Limit reached
//       if (selected.length >= maxSelectable) {
//         if (userPlan !== "enterprise") {
//           setDialogError(
//             `Your ${userPlan} plan allows selecting up to ${maxSelectable} URLs`
//           );
//         }
//         return;
//       }

//       updateFormData({
//         selectedSitemapLinks: [...selected, url],
//       });
//       setDialogError("");
//     };

//     /* ---------------- DIALOG DONE ---------------- */
//     const handleDialogDone = () => {
//       if (!formData.selectedSitemapLinks?.length) {
//         setErrors({
//           websiteUrl: "Select at least one website page",
//         });
//         return;
//       }
//       setOpenDialog(false);
//     };

//     /* ---------------- STEP VALIDATION ---------------- */
//     useImperativeHandle(ref, () => ({
//       validateStep: () => {
//         const newErrors = {};

//         if (!formData.knowledgeName?.trim())
//           newErrors.knowledgeName = "Knowledge Base name is required";
//         else if (!isOnlyLetters(formData.knowledgeName))
//           newErrors.knowledgeName = "Only letters and spaces allowed";

//         if (!formData.title?.trim())
//           newErrors.title = "Widget title is required";
//         else if (!isOnlyLetters(formData.title))
//           newErrors.title = "Only letters and spaces allowed";

//         if (!formData.websiteUrl?.trim())
//           newErrors.websiteUrl = "Website URL is required";
//         else if (!isValidUrl(formData.websiteUrl))
//           newErrors.websiteUrl = "Enter a valid website URL";

//         if (!formData.contactEmail?.trim())
//           newErrors.contactEmail = "Email is required";
//         else if (!isValidEmail(formData.contactEmail))
//           newErrors.contactEmail = "Enter a valid email";

//         if (!formData.phoneNumber?.trim())
//           newErrors.phoneNumber = "Phone number is required";
//         else if (!isValidPhone(formData.phoneNumber))
//           newErrors.phoneNumber = "Enter a valid phone number";

//         if (!formData.selectedSitemapLinks?.length) {
//           newErrors.websiteUrl = "Select at least one website page";
//         }

//         setErrors(newErrors);
//         return Object.keys(newErrors).length === 0;
//       },
//     }));

//     const selectedCount = formData.selectedSitemapLinks?.length || 0;

//     return (
//       <Box display="flex" flexDirection="column" gap={3}>
//         {/* ---------- FORM FIELDS ---------- */}
//         <TextField
//           label="Knowledge Base Name *"
//           value={formData.knowledgeName || ""}
//           onChange={(e) => handleChange("knowledgeName", e.target.value)}
//           error={!!errors.knowledgeName}
//           helperText={errors.knowledgeName}
//           fullWidth
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <TitleIcon />
//               </InputAdornment>
//             ),
//           }}
//         />

//         <TextField
//           label="Description"
//           value={formData.description || ""}
//           onChange={(e) => handleChange("description", e.target.value)}
//           multiline
//           minRows={3}
//           fullWidth
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <DescriptionIcon />
//               </InputAdornment>
//             ),
//           }}
//         />

//         <TextField
//           label="Email *"
//           value={formData.contactEmail || ""}
//           onChange={(e) => handleChange("contactEmail", e.target.value)}
//           error={!!errors.contactEmail}
//           helperText={errors.contactEmail}
//           fullWidth
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <EmailIcon />
//               </InputAdornment>
//             ),
//           }}
//         />

//         <TextField
//           label="Phone Number *"
//           value={formData.phoneNumber || ""}
//           onChange={(e) => handleChange("phoneNumber", e.target.value)}
//           error={!!errors.phoneNumber}
//           helperText={errors.phoneNumber}
//           fullWidth
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <PhoneIcon />
//               </InputAdornment>
//             ),
//           }}
//         />

//         <TextField
//           label="Widget Title *"
//           value={formData.title || ""}
//           onChange={(e) => handleChange("title", e.target.value)}
//           error={!!errors.title}
//           helperText={errors.title}
//           fullWidth
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <TitleIcon />
//               </InputAdornment>
//             ),
//           }}
//         />

//         <TextField
//           label="Website URL *"
//           value={formData.websiteUrl || ""}
//           onChange={(e) => handleChange("websiteUrl", e.target.value)}
//           onBlur={fetchSitemapLinks}
//           error={!!errors.websiteUrl}
//           helperText={errors.websiteUrl}
//           fullWidth
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <LanguageIcon />
//               </InputAdornment>
//             ),
//             endAdornment: loading && (
//               <InputAdornment position="end">
//                 <CircularProgress size={20} />
//               </InputAdornment>
//             ),
//           }}
//         />

//         {/* ---------- SITEMAP DIALOG ---------- */}
//         <Dialog open={openDialog} maxWidth="md" fullWidth>
//           <DialogTitle sx={{ position: "relative", pr: 5 }}>
//             Select Website Pages
//             <IconButton
//               onClick={() => setOpenDialog(false)}
//               sx={{ position: "absolute", right: 8, top: 8 }}
//             >
//               <CloseIcon />
//             </IconButton>
//           </DialogTitle>

//           <DialogContent dividers>
//             <Typography variant="body2" mb={1}>
//               Selected {selectedCount}
//               {userPlan !== "enterprise" && ` / ${maxSelectable}`}
//             </Typography>

//             {dialogError && (
//               <Alert severity="warning" sx={{ mb: 2 }}>
//                 {dialogError}
//               </Alert>
//             )}

//             <List>
//               {(formData.sitemapLinks || [])
//                 .slice((page - 1) * rowsPerPage, page * rowsPerPage)
//                 .map((url) => {
//                   const isChecked =
//                     formData.selectedSitemapLinks?.includes(url);
//                   const isDisabled =
//                     !isChecked &&
//                     userPlan !== "enterprise" &&
//                     selectedCount >= maxSelectable;

//                   return (
//                     <ListItem key={url}>
//                       <Checkbox
//                         checked={isChecked}
//                         disabled={isDisabled}
//                         onChange={() => toggleSitemapLink(url)}
//                       />
//                       <ListItemText primary={url} />
//                     </ListItem>
//                   );
//                 })}
//             </List>

//             <Box display="flex" justifyContent="center" mt={2}>
//               <Pagination
//                 page={page}
//                 onChange={(_, value) => setPage(value)}
//                 count={Math.ceil(
//                   (formData.sitemapLinks?.length || 0) / rowsPerPage
//                 )}
//               />
//             </Box>
//           </DialogContent>

//           <DialogActions>
//             <Button onClick={handleDialogDone}>Done</Button>
//           </DialogActions>
//         </Dialog>
//       </Box>
//     );
//   }
// );

// SimplifiedWelcomeStep.displayName = "SimplifiedWelcomeStep";
// export default SimplifiedWelcomeStep;
